import {AsyncPipe, DecimalPipe, NgClass, NgFor, NgIf} from '@angular/common';
import {Component, OnDestroy, OnInit} from '@angular/core';
import {Router, RouterLink} from '@angular/router';
import { Store } from '@ngrx/store';
import { HeaderComponent } from 'src/app/common/header/header.component';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FilterTagsComponent } from '../../../../shared/filter-builder/filter-tags/filter-tags.component';
import { FilterComboboxMultiselectComponent } from '../../../../shared/filter-builder/filter-combobox-multiselect/filter-combobox-multiselect.component';

import {
  selectAdvancedSearchFailure,
  selectFilterTypes,
  selectLoading as selectPeopleLoading,
  selectPeople, selectPeopleResults, selectPeopleTotal,
  selectSearchFilters,
  selectSelectedCompany
} from "../../../../state/advancedSearch/advanced-search.selectors";

import {selectNewSearchFailure, selectNewSearchSuccess} from "../../../../state/search/search.selectors";
import {distinctUntilChanged, filter, Observable, Subscription, take, combineLatest} from "rxjs";
import {AdvancedSearchPaginationComponent} from "../../advanced-search-pagination/advanced-search-pagination.component";
import { Pagination } from '../../advanced-search-pagination/pagination.model';
import {clearPeople, clearSearchResults, searchPeople, saveSearchFilters} from "../../../../state/advancedSearch/advanced-search.actions";
import {EventTypes} from "../../../../event-types";
import {newSearch} from "../../../../state/search/search.actions";
import {SharedService} from "../../../../services/shared.service";
import {AuthService} from "../../../../services/auth.service";
import {PopupService} from "../../../../services/popup.service";
import {WorkspaceSelectionModalComponent} from "./workspace-selection-modal/workspace-selection-modal.component";
import {Workspace} from "../../../../models/workspace.model";
import {CompanyDetailsModalComponent} from "../../company-details-modal/company-details-modal.component";
import {MatTooltipModule} from "@angular/material/tooltip";

interface Company {
  company_name: string;
  main_address?: {
    city?: string;
    region?: string;
    country?: string;
  };
  veridion_id: string;
  website_url?: string;
  website_domain?: string;
  linkedin_url?: string;
  year_founded?: number;
  num_locations?: number;
  employee_count?: {
    value?: number;
  };
  revenue?: {
    value?: number;
  };
}

@Component({
  selector: 'app-people',
  standalone: true,
  imports: [HeaderComponent, NgFor, NgIf, RouterLink, AsyncPipe, AdvancedSearchPaginationComponent, NgClass, DecimalPipe, WorkspaceSelectionModalComponent, FormsModule, ReactiveFormsModule, FilterTagsComponent, FilterComboboxMultiselectComponent, CompanyDetailsModalComponent, MatTooltipModule],
  templateUrl: './people.component.html',
  styleUrl: './people.component.scss',
})
export class PeopleComponent implements OnInit, OnDestroy {

  user: any;

  pagination: Pagination = {
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0
  };

  // Track the requested page locally
  private requestedPage: number = 1;

  // Track if we have received data
  hasData: boolean = false;

  // Add local loading flag
  isInitialLoading: boolean = true;

  // Enough skeleton rows to overfill the tallest realistic viewport; the
  // full-height skeleton list clips the excess (overflow-hidden).
  protected readonly skeletonRows = Array.from({ length: 20 }, (_, i) => i);

  peopleResults$: Observable<any>;
  people$: Observable<any[]>;
  totalPeople$: Observable<number>;
  loading$: Observable<boolean>;
  selectedCompany$: Observable<Company>;

  searchFilters$: Observable<any>;
  filterTypes$: Observable<any[]>;

  newSearchSuccessSubscription: Subscription = null;
  newSearchFailureSubscription: Subscription = null;

  companyLocation: string = 'Using company location';

  // Person filter state
  personTitles: string[] = [];
  personSeniorities: string[] = [];

  // FormControls for filter components
  personTitlesControl: FormControl = new FormControl([]);
  personSenioritiesControl: FormControl = new FormControl([]);

  // Seniority options (matching advanced-search)
  seniorityOptions = [
    { id: 1, name: 'owner' },
    { id: 2, name: 'founder' },
    { id: 3, name: 'c_suite' },
    { id: 4, name: 'partner' },
    { id: 5, name: 'vp' },
    { id: 6, name: 'head' },
    { id: 7, name: 'director' },
    { id: 8, name: 'manager' },
    { id: 9, name: 'senior' },
    { id: 10, name: 'entry' },
    { id: 11, name: 'intern' }
  ];

  private subscription: Subscription = new Subscription();
  private peopleResultsSubscription: Subscription;

  // Modal state
  showWorkspaceModal = false;
  selectedPersonForReport: any = null;

  // Company details modal state
  isCompanyModalVisible = false;
  selectedCompanyForModal: any = null;

  constructor(
    private store: Store,
    private authService : AuthService,
    private sharedService: SharedService,
    private popupService: PopupService,
    private routerService: Router,
  ) {
    this.selectedCompany$ = this.store.select(selectSelectedCompany);
    this.searchFilters$ = this.store.select(selectSearchFilters);
    this.filterTypes$ = this.store.select(selectFilterTypes);
    this.peopleResults$ = this.store.select(selectPeopleResults);
    this.people$ = this.store.select(selectPeople);
    this.totalPeople$ = this.store.select(selectPeopleTotal);
    // Bind to the people-search loading flag (state.advancedSearch.loading), NOT
    // state.search.loading. The latter is toggled by newSearch (report generation),
    // which would hide the list/pagination and permanently clear hasData when a
    // report is generated. See onPageChanged/searchPeople for the people flag.
    this.loading$ = this.store.select(selectPeopleLoading);
  }

  ngOnInit(): void {
    this.authService.authUser().subscribe(user => {
      this.user = user;
    });

    // Subscribe to people results and update pagination
    this.peopleResultsSubscription = this.peopleResults$.subscribe(results => {
      // console.log('People results received:', results);

      // Turn off initial loading when we get any results (even empty)
      if (results !== null) {
        this.isInitialLoading = false;
      }

      if (results && results.pagination) {
        // Update pagination with data from the store but keep the requested page
        this.pagination = {
          current_page: this.requestedPage, // Use the locally tracked page
          last_page: results.pagination.total_pages || 1,
          per_page: results.pagination.per_page || 10,
          total: results.pagination.total_entries || 0
        };

        // Mark that we have data
        this.hasData = true;

      } else {
        // Reset pagination if no results
        this.pagination = {
          current_page: 1,
          last_page: 1,
          per_page: 10,
          total: 0
        };
        this.requestedPage = 1;
        this.hasData = false;
      }
    });

    // Subscribe to loading state to reset hasData when loading starts
    this.subscription.add(
      this.loading$.subscribe(loading => {
        if (loading) {
          this.hasData = false;
        }
      })
    );

    this.subscription.add(this.peopleResultsSubscription);

    // If the people search fails (e.g. the provider is unavailable), stop the skeleton
    // and surface a non-blocking notice instead of leaving an endless loader.
    this.subscription.add(
      this.store.select(selectAdvancedSearchFailure)
        .pipe(filter(error => !!error))
        .subscribe(() => {
          this.isInitialLoading = false;
          this.hasData = false;
          this.popupService.notify(
            'This functionality is currently not available. Please try again later.',
            'Advanced Search',
            'warning'
          );
        })
    );

    this.subscription.add(
      this.sharedService.peopleEventEmitter.subscribe(event => {
        if (event.type === EventTypes.SHOW_REPORT_BANNER) {
          const reportId = event.data.report_id;
          const url = this.routerService.serializeUrl(
            this.routerService.createUrlTree(['/report/' + reportId])
          );
          window.open(url, '_blank');
        }
      })
    );

    // Subscribe to selected company changes to reset pagination
    this.subscription.add(
      this.selectedCompany$.pipe(
        distinctUntilChanged((prev, curr) => prev?.veridion_id === curr?.veridion_id)
      ).subscribe(company => {
        if (company) {
          this.requestedPage = 1;
          this.pagination = {
            ...this.pagination,
            current_page: 1
          };
        }
      })
    );

    // Extract company location from filters and initialize person filters
    this.subscription.add(
      combineLatest([this.searchFilters$, this.filterTypes$]).subscribe(([filters, filterTypes]) => {
        if (filters && filters.filters) {
          const locationFilter = filters.filters.find((filter: any) => filter.filterType === 10);

          if (locationFilter && locationFilter.value) {
            const locationParts = [];

            if (locationFilter.value.countryData) {
              locationParts.push(locationFilter.value.countryData.name);
            }

            if (locationFilter.value.regionData) {
              locationParts.push(locationFilter.value.regionData.name);
            }

            if (locationFilter.value.cityData) {
              locationParts.push(locationFilter.value.cityData.name);
            }

            if (locationParts.length > 0) {
              this.companyLocation = locationParts.join(', ');
            }
          }

          // Extract person filters (IDs 11 and 12)
          filters.filters.forEach((filter: any) => {
            if (!filter.filterType) return;

            const filterType = filterTypes.find((type: any) => type.id === filter.filterType);
            if (!filterType) return;

            // Person Titles (ID 11)
            if (filterType.id === 11 && Array.isArray(filter.value)) {
              this.personTitles = [...filter.value];
              this.personTitlesControl.setValue([...filter.value], { emitEvent: false });
            }

            // Person Seniorities (ID 12)
            if (filterType.id === 12 && Array.isArray(filter.value)) {
              this.personSeniorities = [...filter.value];
              this.personSenioritiesControl.setValue([...filter.value], { emitEvent: false });
            }
          });
        }
      })
    );

    // Subscribe to FormControl changes
    this.subscription.add(
      this.personTitlesControl.valueChanges.subscribe(value => {
        this.personTitles = Array.isArray(value) ? value : [];
      })
    );

    this.subscription.add(
      this.personSenioritiesControl.valueChanges.subscribe(value => {
        this.personSeniorities = Array.isArray(value) ? value : [];
      })
    );
  }

  onPageChanged(page: number) {
    // console.log('Page changed to:', page);

    // Update the locally tracked page
    this.requestedPage = page;

    // Don't update pagination immediately - wait for API response
    // this.hasData will be set to false by loading state

    this.selectedCompany$.pipe(take(1)).subscribe(selectedCompany => {
      if (selectedCompany) {
        // Get current search filters
        this.searchFilters$.pipe(take(1)).subscribe(formValue => {
          this.filterTypes$.pipe(take(1)).subscribe(filterTypes => {
            // Extract person filters from the saved filters
            let personTitles: string[] = [];
            let personSeniorities: string[] = [];
            let companyLocation: string = '';

            if (formValue && formValue.filters) {
              formValue.filters.forEach((filter: any) => {
                if (!filter.filterType) return;

                const filterType = filterTypes.find((type: any) => type.id === filter.filterType);
                if (!filterType) return;

                // Check if this is a location filter
                if (filterType.id === 10 || filterType.attribute === 'company_location') {
                  if (filter.value) {
                    const locationParts = [];
                    if (filter.value.countryData) locationParts.push(filter.value.countryData.name);
                    if (filter.value.regionData) locationParts.push(filter.value.regionData.name);
                    if (filter.value.cityData) locationParts.push(filter.value.cityData.name);
                    if (locationParts.length > 0) {
                      companyLocation = locationParts.join(', ');
                    }
                  }
                }

                // Check if this is a person filter
                if (filterType.applied === 'person' || filterType.id === 11 || filterType.id === 12) {
                  if (filterType.attribute === 'person_titles' && Array.isArray(filter.value)) {
                    personTitles = filter.value;
                  } else if (filterType.attribute === 'person_seniorities' && Array.isArray(filter.value)) {
                    personSeniorities = filter.value;
                  }
                }
              });
            }

            // Build the search params
            const searchParams: any = {
              companyName: selectedCompany.company_name,
              selectedCompany: selectedCompany,
              page,
              perPage: this.pagination.per_page
            };

            // Use by-domain search when website_domain is available
            if (selectedCompany.website_domain) {
              searchParams.website_domain = selectedCompany.website_domain;
            }

            // Add person filters if they exist
            if (personTitles.length > 0) {
              searchParams.person_titles = personTitles;
            }
            if (personSeniorities.length > 0) {
              searchParams.person_seniorities = personSeniorities;
            }

            // Add location only for fuzzy search (no website_domain)
            if (!selectedCompany.website_domain) {
              if (companyLocation) {
                searchParams.organization_locations = companyLocation.split(',').map(item => item.trim());
              } else if (selectedCompany.main_address) {
                const locationString = this.getLocation(selectedCompany.main_address);
                searchParams.organization_locations = locationString.split(',').map(item => item.trim());
              }
            }

            // console.log('Dispatching searchPeople with params:', searchParams);

            this.store.dispatch(searchPeople({
              params: searchParams
            }));
          });
        });
      }
    });
  }

  onPerPageChanged(pagination: Pagination) {
    // console.log('Per page changed:', pagination);

    // Reset to page 1 when changing per page
    this.requestedPage = 1;

    this.selectedCompany$.pipe(take(1)).subscribe(selectedCompany => {
      if (selectedCompany) {
        // Get current search filters
        this.searchFilters$.pipe(take(1)).subscribe(formValue => {
          this.filterTypes$.pipe(take(1)).subscribe(filterTypes => {
            // Extract person filters from the saved filters
            let personTitles: string[] = [];
            let personSeniorities: string[] = [];
            let companyLocation: string = '';

            if (formValue && formValue.filters) {
              formValue.filters.forEach((filter: any) => {
                if (!filter.filterType) return;

                const filterType = filterTypes.find((type: any) => type.id === filter.filterType);
                if (!filterType) return;

                // Check if this is a location filter
                if (filterType.id === 10 || filterType.attribute === 'company_location') {
                  if (filter.value) {
                    const locationParts = [];
                    if (filter.value.countryData) locationParts.push(filter.value.countryData.name);
                    if (filter.value.regionData) locationParts.push(filter.value.regionData.name);
                    if (filter.value.cityData) locationParts.push(filter.value.cityData.name);
                    if (locationParts.length > 0) {
                      companyLocation = locationParts.join(', ');
                    }
                  }
                }

                // Check if this is a person filter
                if (filterType.applied === 'person' || filterType.id === 11 || filterType.id === 12) {
                  if (filterType.attribute === 'person_titles' && Array.isArray(filter.value)) {
                    personTitles = filter.value;
                  } else if (filterType.attribute === 'person_seniorities' && Array.isArray(filter.value)) {
                    personSeniorities = filter.value;
                  }
                }
              });
            }

            // Build the search params
            const searchParams: any = {
              companyName: selectedCompany.company_name,
              selectedCompany: selectedCompany,
              page: pagination.current_page,
              perPage: pagination.per_page
            };

            // Use by-domain search when website_domain is available
            if (selectedCompany.website_domain) {
              searchParams.website_domain = selectedCompany.website_domain;
            }

            // Add person filters if they exist
            if (personTitles.length > 0) {
              searchParams.person_titles = personTitles;
            }
            if (personSeniorities.length > 0) {
              searchParams.person_seniorities = personSeniorities;
            }

            // Add location only for fuzzy search (no website_domain)
            if (!selectedCompany.website_domain) {
              if (companyLocation) {
                searchParams.organization_locations = companyLocation.split(',').map(item => item.trim());
              } else if (selectedCompany.main_address) {
                const locationString = this.getLocation(selectedCompany.main_address);
                searchParams.organization_locations = locationString.split(',').map(item => item.trim());
              }
            }

            this.store.dispatch(searchPeople({
              params: searchParams
            }));
          });
        });
      }
    });
  }

  /**
   * Search with updated person filters
   */
  onPersonFiltersSearch(): void {
    // First, update the saved filters in the store
    this.updatePersonFiltersInState();

    // Reset to page 1 when searching with new filters
    this.requestedPage = 1;

    this.selectedCompany$.pipe(take(1)).subscribe(selectedCompany => {
      if (selectedCompany) {
        // Get current search filters for location
        this.searchFilters$.pipe(take(1)).subscribe(formValue => {
          this.filterTypes$.pipe(take(1)).subscribe(filterTypes => {
            let companyLocation: string = '';

            // Extract location from saved filters
            if (formValue && formValue.filters) {
              formValue.filters.forEach((filter: any) => {
                if (!filter.filterType) return;

                const filterType = filterTypes.find((type: any) => type.id === filter.filterType);
                if (!filterType) return;

                if (filterType.id === 10 || filterType.attribute === 'company_location') {
                  if (filter.value) {
                    const locationParts = [];
                    if (filter.value.countryData) locationParts.push(filter.value.countryData.name);
                    if (filter.value.regionData) locationParts.push(filter.value.regionData.name);
                    if (filter.value.cityData) locationParts.push(filter.value.cityData.name);
                    if (locationParts.length > 0) {
                      companyLocation = locationParts.join(', ');
                    }
                  }
                }
              });
            }

            // Build search params with current person filter values
            const searchParams: any = {
              companyName: selectedCompany.company_name,
              selectedCompany: selectedCompany,
              page: 1,
              perPage: this.pagination.per_page
            };

            // Use by-domain search when website_domain is available
            if (selectedCompany.website_domain) {
              searchParams.website_domain = selectedCompany.website_domain;
            }

            // Add person filters from local state
            if (this.personTitles.length > 0) {
              searchParams.person_titles = this.personTitles;
            }
            if (this.personSeniorities.length > 0) {
              searchParams.person_seniorities = this.personSeniorities;
            }

            // Add location only for fuzzy search (no website_domain)
            if (!selectedCompany.website_domain) {
              if (companyLocation) {
                searchParams.organization_locations = companyLocation.split(',').map(item => item.trim());
              } else if (selectedCompany.main_address) {
                const locationString = this.getLocation(selectedCompany.main_address);
                searchParams.organization_locations = locationString.split(',').map(item => item.trim());
              }
            }

            // Dispatch search
            this.store.dispatch(searchPeople({
              params: searchParams
            }));
          });
        });
      }
    });
  }

  /**
   * Update person filters in NgRx state for persistence
   */
  updatePersonFiltersInState(): void {
    combineLatest([this.searchFilters$, this.filterTypes$])
      .pipe(take(1))
      .subscribe(([savedFilters, filterTypes]) => {
        if (!savedFilters || !savedFilters.filters) return;

        // Clone the saved filters
        const updatedFilters = { ...savedFilters };
        const filtersArray = [...updatedFilters.filters];

        // Update or add Person Titles filter (ID 11)
        const titleFilterIndex = filtersArray.findIndex((f: any) => f.filterType === 11);
        if (this.personTitles.length > 0) {
          if (titleFilterIndex > -1) {
            filtersArray[titleFilterIndex] = {
              ...filtersArray[titleFilterIndex],
              value: [...this.personTitles]
            };
          } else {
            filtersArray.push({
              filterType: 11,
              value: [...this.personTitles]
            });
          }
        } else if (titleFilterIndex > -1) {
          // Remove filter if empty
          filtersArray.splice(titleFilterIndex, 1);
        }

        // Update or add Person Seniorities filter (ID 12)
        const seniorityFilterIndex = filtersArray.findIndex((f: any) => f.filterType === 12);
        if (this.personSeniorities.length > 0) {
          if (seniorityFilterIndex > -1) {
            filtersArray[seniorityFilterIndex] = {
              ...filtersArray[seniorityFilterIndex],
              value: [...this.personSeniorities]
            };
          } else {
            filtersArray.push({
              filterType: 12,
              value: [...this.personSeniorities]
            });
          }
        } else if (seniorityFilterIndex > -1) {
          // Remove filter if empty
          filtersArray.splice(seniorityFilterIndex, 1);
        }

        // Update the filters in the state
        updatedFilters.filters = filtersArray;

        // Dispatch the updated filters to the store
        this.store.dispatch(saveSearchFilters({
          filters: updatedFilters,
          filterTypes: filterTypes
        }));
      });
  }

  /**
   * Clear person filters and search again
   */
  clearPersonFilters(): void {
    this.personTitles = [];
    this.personSeniorities = [];
    this.personTitlesControl.setValue([]);
    this.personSenioritiesControl.setValue([]);
    this.onPersonFiltersSearch();
  }

  generateReport(person: any) {

    const linkedinUrl = person.linkedin_url;
    const linkedinUrlValid = this.isLinkedinUrl(linkedinUrl);

    if (linkedinUrlValid) {
      // Store the person data and show workspace selection modal
      this.selectedPersonForReport = person;
      this.showWorkspaceModal = true;
    }
  }

  isLinkedinUrl(value: string): boolean {
    const linkedinRegex = /^https?:\/\/www\.linkedin\.com\/(?!.*https?:\/\/www\.linkedin\.com\/).*$/;
    return linkedinRegex.test(value);
  }

  goToReport(person: any) {
  }

  getLocation(address: any): string {
    if (!address) return 'Location not available';

    const parts = [
      address.city,
      address.region,
      address.country
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(', ') : 'Location not available';
  }

  getSelectedFilters(formValue: any): any[] {
    const filtersArray = formValue?.filters || [];
    return filtersArray.filter((filter: any) => filter.filterType && filter.value);
  }

  getFilterLabel(filterId: number, types: any[]): string {
    const filterType = types.find(type => type.id === filterId);
    return filterType?.name || '';
  }

  getFilterValue(filter: any, types: any[]): string {
    const filterType = types.find(type => type.id === filter.filterType);

    // Handle location filter (ID 10)
    if (filterType?.id === 10 && filter.value) {
      const locationParts = [];

      if (filter.value.countryData) {
        locationParts.push(filter.value.countryData.name);
      } else if (filter.value.country) {
        locationParts.push("Country");
      }

      if (filter.value.regionData) {
        locationParts.push(filter.value.regionData.name);
      } else if (filter.value.region) {
        locationParts.push("Region");
      }

      if (filter.value.cityData) {
        locationParts.push(filter.value.cityData.name);
      } else if (filter.value.city) {
        locationParts.push("City");
      }

      return locationParts.length > 0 ? locationParts.join(', ') : 'Any location';
    }

    // Handle select and combobox-multiselect filters
    if (filterType?.type === 'select' || filterType?.type === 'combobox-multiselect') {
      if (Array.isArray(filter.value)) {
        return filter.value.join(', ');
      } else {
        const option = filterType.options?.find(opt => opt.id === filter.value);
        return option?.name || filter.value?.toString() || '';
      }
    }

    // Handle range filter
    if (filterType?.type === 'range' && Array.isArray(filter.value)) {
      const [min, max] = filter.value;
      if (min !== null && max !== null) {
        return `${min} - ${max}`;
      } else if (min !== null) {
        return `≥ ${min}`;
      } else if (max !== null) {
        return `≤ ${max}`;
      }
    }

    // Handle tags filter
    if (filterType?.type === 'tags' && Array.isArray(filter.value)) {
      return filter.value.join(', ');
    }

    return filter.value?.toString() || '';
  }

  commitSearch(linkedinUrl: string, workspaceId?: string) {
    // console.log('[people.component.ts] commitSearch')

    this.sharedService.emitEvent(EventTypes.SHOW_LOADING_REPORT_BANNER, { message: 'Loading Report Banner' });

    let params: any = {
      linkedin_profile: linkedinUrl,
    };

    // Add workspace_id if provided
    const searchData: any = {
      user_id: this.user.id,
      linkedin_profile: linkedinUrl
    };

    if (workspaceId) {
      searchData.workspace_id = workspaceId;
    }

    this.store.dispatch(newSearch(searchData));

    const newSearchSuccess$ = this.store.select(selectNewSearchSuccess);
    const newSearchFailure$ = this.store.select(selectNewSearchFailure);

    this.newSearchSuccessSubscription = newSearchSuccess$.pipe(
      filter(response => response !== null),
      take(1)
    ).subscribe({
      next: (response) => {
        if (this.newSearchSuccessSubscription) {
          this.newSearchSuccessSubscription.unsubscribe();
        }
      },
      error: (error) => {

        if (this.newSearchSuccessSubscription) {
          this.newSearchSuccessSubscription.unsubscribe();
        }
      }
    });

    this.newSearchFailureSubscription = newSearchFailure$.pipe(
      filter(failure => failure !== null),
      take(1)
    ).subscribe({
      next: (error) => {
        const errorMessage = error.error.message

        this.popupService.notify(errorMessage, 'Search error', 'error');

        this.sharedService.emitEvent(EventTypes.CLOSE_REPORT_BANNER, { message: 'Close Report Banner' });

        if (this.newSearchFailureSubscription) {
          this.newSearchFailureSubscription.unsubscribe();
        }
      },
      error: (error) => {

        if (this.newSearchFailureSubscription) {
          this.newSearchFailureSubscription.unsubscribe();
        }
      }
    });
  }

  isPersonFilter(filterId: number, types: any[]): boolean {
    if (filterId === 11 || filterId === 12) {
      return true;
    }

    const filterType = types.find(type => type.id === filterId);
    if (filterType && filterType.applied === 'person') {
      return true;
    }

    if (filterType && (
      filterType.attribute === 'person_titles' ||
      filterType.attribute === 'person_seniorities'
    )) {
      return true;
    }

    return false;
  }

  // Modal event handlers
  onReportGenerated(workspace: Workspace): void {

    if (this.selectedPersonForReport) {
      const linkedinUrl = this.selectedPersonForReport.linkedin_url;

      // Close modal
      this.showWorkspaceModal = false;

      // Start search with selected workspace
      this.commitSearch(linkedinUrl, workspace.id);

      // Reset modal state
      this.selectedPersonForReport = null;
    }
  }

  onModalClosed(): void {
    this.showWorkspaceModal = false;
    this.selectedPersonForReport = null;
  }

  /**
   * Open company details modal
   */
  openCompanyDetails(company: any): void {
    this.selectedCompanyForModal = company;
    this.isCompanyModalVisible = true;
  }

  /**
   * Close company details modal
   */
  closeCompanyModal(): void {
    this.isCompanyModalVisible = false;
    this.selectedCompanyForModal = null;
  }

  /**
   * Format employee count for display (e.g., 1500 -> 1.5K)
   */
  formatEmployeeCount(count: number | string): string {
    if (!count) return '';

    const num = typeof count === 'string' ? parseInt(count) : count;
    if (isNaN(num)) return '';

    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  }

  /**
   * Format revenue for display (e.g., 1500000 -> 1.5M)
   */
  formatRevenue(revenue: number | string): string {
    if (!revenue) return '';

    const num = typeof revenue === 'string' ? parseFloat(revenue) : revenue;
    if (isNaN(num)) return '';

    if (num >= 1000000000) {
      const billions = Math.round(num / 1000000000);
      return billions + 'B';
    } else if (num >= 1000000) {
      const millions = Math.round(num / 1000000);
      return millions + 'M';
    } else if (num >= 1000) {
      const thousands = Math.round(num / 1000);
      return thousands + 'K';
    }
    return num.toString();
  }

  // In people.component.ts, update the ngOnDestroy method:

  ngOnDestroy() {
    // console.log('[people.component.ts] ngOnDestroy');

    // Clear only people data, not search results (companies)
    this.store.dispatch(clearPeople());
    // REMOVED: this.store.dispatch(clearSearchResults()); // Don't clear companies data!

    // Reset local state
    this.requestedPage = 1;
    this.hasData = false;
    this.isInitialLoading = false;

    // Unsubscribe from all subscriptions
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    if (this.newSearchSuccessSubscription) {
      this.newSearchSuccessSubscription.unsubscribe();
    }

    if (this.newSearchFailureSubscription) {
      this.newSearchFailureSubscription.unsubscribe();
    }
  }

}
